import { apiFetch } from '../../../services/api';

// ---------------------------------------------------------------------------
// Types matching the backend response shape
// ---------------------------------------------------------------------------

export interface ApiOption {
  id: string;
  text: string;
  is_correct: boolean;
}

export interface ApiQuestion {
  question_bank_id: number;
  question_order: number;
  source_table: string;
  source_id: number;
  question_type: string;
  question_text: string;
  sdg_numbers: number[];
  options: ApiOption[];
  already_answered?: boolean;
  is_correct?: boolean | null;
}

export interface SessionResponse {
  session_id: string;
  current_round: number;
  questions: ApiQuestion[];
}

export interface SharedQuizMeta {
  id: string;
  title: string;
  description: string;
  join_code: string;
  is_active: boolean;
  rounds: number;
  questions_per_round: number;
  question_count: number;
  required_question_count: number;
  selected_question_ids: number[];
  max_attempts_per_user: number | null;
  attempts_used: number | null;
  attempts_allowed: number | null;
}

export interface QuizParticipant {
  user_id: number;
  username: string;
  attempts_used: number;
  best_score: number;
  extra_attempts: number;
  attempts_allowed: number | null;
}

export interface CustomQuizJoinResponse extends SessionResponse {
  custom_quiz: SharedQuizMeta;
}

export type SessionVersion = 'short' | 'long';

export interface AnswerResponse {
  question_id: number;
  is_correct: boolean;
  correct_answer_index: number;
  session: {
    score: number;
    questions_correct: number;
    earth_vitality: number;
    vitality_delta?: number;
  };
  round_progress: {
    answered: number;
    total: number;
    complete: boolean;
  };
}

export interface GameSettingsResponse {
  id: number;
  reading_time_seconds: number;
  decay_duration_seconds: number;
  max_vitality_gain: number;
  min_vitality_gain: number;
  vitality_loss: number;
  game_duration_seconds: number;
}

export interface ReportQuestionResponse {
  accepted: boolean;
  report_id: string;
  question_id: string;
  reason_text: string;
  details: string;
  reported_at: string;
  message: string;
}

export interface LeaderboardEntry {
  rank?: number;
  session_id: string;
  user_id: number | null;
  display_name: string;
  game_version: string;
  score: number;
  earth_vitality: number;
  questions_correct?: number;
  questions_total?: number;
  email?: string;
  ended_at: string | null;
}

export interface LeaderboardResponse {
  results: LeaderboardEntry[];
}

export interface QuizLeaderboardResponse extends LeaderboardResponse {
  quiz_id: string;
  current_user: LeaderboardEntry | null;
}

export type GameAnalyticsTimePeriod = 'last_year' | 'last_6_months' | 'last_week' | 'last_day' | 'all_time';

export interface GameAnalyticsEngagementPoint {
  label: string;
  completed_sessions: number;
}

export interface GameAnalyticsOverviewResponse {
  time_period: GameAnalyticsTimePeriod;
  completed_sessions: number;
  average_score: number;
  average_time_per_question_seconds: number;
  engagement_series: GameAnalyticsEngagementPoint[];
}

export type LeaderboardSortBy = 'vitality' | 'score';

export interface GameSessionSummaryResponse {
  session_id: string;
  game_version: string;
  custom_quiz?: SharedQuizMeta | null;
  questions_total: number;
  questions_answered: number;
  questions_correct: number;
  accuracy_percent: number;
  score: number;
  earth_vitality: number;
  scoring_version: string;
  sdgs_surfaced: number[];
  started_at: string;
  ended_at: string | null;
}

export interface GameSessionDetailResponse {
  session_id: string;
  current_round: number;
  status: 'active' | 'completed';
  questions_total: number;
  questions_answered: number;
  questions_correct: number;
  score: number;
  earth_vitality: number;
  year: number;
  started_at: string;
  ended_at: string | null;
}

export interface CurrentRoundQuestionsResponse {
  session_id: string;
  current_round: number;
  questions: ApiQuestion[];
}

// ---------------------------------------------------------------------------
// Admin / Question Bank types
// ---------------------------------------------------------------------------

export interface StaticQuestionListItem {
  id: number;
  question_text: string;
  sdg_numbers: number[];
  question_type: string;
  guardian: string;
}

export interface PaginatedStaticQuestionResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: StaticQuestionListItem[];
}

export interface GetStaticQuestionsParams {
  search?: string;
  sdg?: number[];
  page?: number;
  pageSize?: number;
  ordering?: string;
}

export interface StaticQuestionOption {
  id: number;
  text: string;
  is_correct: boolean;
}

export interface StaticQuestionDetail {
  id: number;
  question_text: string;
  options: StaticQuestionOption[];
  correct_answer_index: number;
  sdg_numbers: number[];
  question_type: string;
  guardian: string;
}

export interface StaticQuestionCreatePayload {
  question_text: string;
  options: { text: string; is_correct: boolean }[];
  sdg_numbers: number[];
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

/**
 * Create a new game session and get round 1 questions.
 * @param count Number of questions to fetch (1–30, default 15)
 * @param version Game session version for backend scoring/access rules
 */
export async function createGameSession(
  count = 15,
  version: 'short' | 'long' = 'short'
): Promise<SessionResponse> {
  const response = await createGameSessionWithOptions(count, version, false);
  return response;
}

export async function createGameSessionWithOptions(
  count = 15,
  version: 'short' | 'long' = 'short',
  forceNewRun = false,
): Promise<SessionResponse> {
  const response = await apiFetch('/api/game/sessions/', {
    method: 'POST',
    body: JSON.stringify({ count, version, force_new_run: forceNewRun }),
  });

  if (!response.ok) {
    throw new Error(`Failed to create session: ${response.status}`);
  }

  return response.json();
}

/**
 * Join a custom game by code and get the session to play.
 * Requires authentication on backend.
 */
export async function joinCustomQuizByCode(joinCode: string): Promise<CustomQuizJoinResponse> {
  const response = await apiFetch('/api/game/custom-quizzes/join/', {
    method: 'POST',
    body: JSON.stringify({ join_code: joinCode.trim() }),
  });

  if (!response.ok) {
    let detail = '';
    try {
      const payload = await response.json();
      detail = typeof payload?.detail === 'string' ? payload.detail : '';
    } catch {
      // Ignore parse failure and fallback to status-based errors.
    }

    if (response.status === 404) {
      throw new Error(detail || 'Invalid code. Please check and try again.');
    }
    if (response.status === 403 || response.status === 401) {
      throw new Error(detail || 'Please log in to play custom games.');
    }
    if (response.status === 400) {
      throw new Error(detail || 'Could not join this custom game.');
    }
    throw new Error(`Failed to join custom game: ${response.status}`);
  }

  return response.json();
}

/**
 * Preview a custom game by code without starting a session.
 * Used by the Lobby screen to show quiz info + leaderboard before committing an attempt.
 * Requires authentication (same as joining).
 */
export async function previewCustomQuiz(joinCode: string): Promise<SharedQuizMeta> {
  const response = await apiFetch(
    `/api/game/custom-quizzes/preview/${encodeURIComponent(joinCode.trim().toUpperCase())}/`,
    { method: 'GET' },
  );

  if (!response.ok) {
    let detail = '';
    try {
      const payload = await response.json();
      detail = typeof payload?.detail === 'string' ? payload.detail : '';
    } catch {
      // Ignore parse failure and fallback to status-based errors.
    }

    if (response.status === 404) {
      throw new Error(detail || 'Invalid code. Please check and try again.');
    }
    if (response.status === 403 || response.status === 401) {
      throw new Error(detail || 'Please log in to view this game.');
    }
    throw new Error(detail || `Failed to preview custom quiz: ${response.status}`);
  }

  return response.json();
}

/**
 * Submit the player's answer for a question.
 * @param sessionId UUID of the current session
 * @param questionId question_bank_id from the session response
 * @param chosenOptionIndex Index of the selected option (0–3)
 */
export async function submitAnswer(
  sessionId: string,
  questionId: number,
  chosenOptionIndex: number,
  elapsedMs: number,
): Promise<AnswerResponse> {
  const response = await apiFetch(
    `/api/game/sessions/${sessionId}/questions/${questionId}/answer/`,
    {
      method: 'POST',
      body: JSON.stringify({
        answer: chosenOptionIndex,
        elapsed_ms: Math.max(0, Math.floor(elapsedMs)),
      }),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to submit answer: ${response.status}`);
  }

  return response.json();
}

export async function getGameSessionSummary(sessionId: string): Promise<GameSessionSummaryResponse> {
  const response = await apiFetch(
    `/api/game/sessions/${sessionId}/summary/`,
    { method: 'GET' },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch game summary: ${response.status}`);
  }

  return response.json();
}

export async function getGameSessionDetail(sessionId: string): Promise<GameSessionDetailResponse> {
  const response = await apiFetch(
    `/api/game/sessions/${sessionId}/`,
    { method: 'GET' },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch game session detail: ${response.status}`);
  }

  return response.json();
}

export async function getCurrentRoundQuestions(sessionId: string): Promise<CurrentRoundQuestionsResponse> {
  const response = await apiFetch(
    `/api/game/sessions/${sessionId}/rounds/current/questions/`,
    { method: 'GET' },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch current round questions: ${response.status}`);
  }

  return response.json();
}

export async function getGameSettings(): Promise<GameSettingsResponse> {
  const response = await apiFetch('/api/game/settings/');

  if (!response.ok) {
    throw new Error(`Failed to get the game settings: ${response.status}`);
  }

  return response.json();
}

export async function updateGameSettings(
  settings: Partial<GameSettingsResponse>
): Promise<GameSettingsResponse> {
  const response = await apiFetch('/api/game/settings/', {
    method: 'PATCH',
    body: JSON.stringify(settings),
  });

  if (!response.ok) {
    throw new Error(`Failed to update the game settings: ${response.status}`);
  }

  return response.json();
}

/**
 * Report a question.
 * @param questionId question ID from current question object
 * @param reasonText report reason text
 */
export async function reportQuestion(
  questionId: string,
  reasonText: string,
  details?: string,
): Promise<ReportQuestionResponse> {
  const response = await apiFetch(
    `/api/game/questions/${questionId}/report/`,
    {
      method: 'POST',
      body: JSON.stringify({ reason: reasonText, details }),
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to report question: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch leaderboard entries for completed long-mode runs.
 */
export async function getLeaderboard(
  limit = 10,
  timePeriod: GameAnalyticsTimePeriod = 'last_6_months',
  sortBy: LeaderboardSortBy = 'vitality',
): Promise<LeaderboardResponse> {
  const response = await apiFetch(
    `/api/game/leaderboard/?limit=${limit}&time_period=${timePeriod}&sort_by=${sortBy}`,
    { method: 'GET' },
    false,
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch leaderboard: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch leaderboard entries for a specific custom quiz.
 */
export async function getQuizLeaderboard(
  quizId: string,
  limit = 10,
  timePeriod: GameAnalyticsTimePeriod = 'last_6_months',
  sortBy: LeaderboardSortBy = 'score',
): Promise<QuizLeaderboardResponse> {
  const response = await apiFetch(
    `/api/game/leaderboard/${quizId}/?limit=${limit}&time_period=${timePeriod}&sort_by=${sortBy}`,
    { method: 'GET' },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch quiz leaderboard: ${response.status}`);
  }

  return response.json();
}

export async function getGameAnalyticsOverview(
  timePeriod: GameAnalyticsTimePeriod = 'last_6_months',
): Promise<GameAnalyticsOverviewResponse> {
  const response = await apiFetch(
    `/api/game/admin-analytics/overview/?time_period=${timePeriod}`,
    { method: 'GET' },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch game analytics overview: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch question bank items with optional search + SDG filters.
 */
export async function getStaticQuestions(
  params: GetStaticQuestionsParams = {},
): Promise<PaginatedStaticQuestionResponse> {
  const searchParams = new URLSearchParams();

  if (params.search && params.search.trim()) {
    searchParams.set('search', params.search.trim());
  }

  if (params.sdg && params.sdg.length > 0) {
    params.sdg.forEach((sdg) => {
      searchParams.append('sdg', String(sdg));
    });
  }

  if (params.page) {
    searchParams.set('page', String(params.page));
  }

  if (params.pageSize) {
    searchParams.set('page_size', String(params.pageSize));
  }

  if (params.ordering) {
    searchParams.set('ordering', params.ordering);
  }

  const queryString = searchParams.toString();
  const url = queryString
    ? `/api/game/questions/?${queryString}`
    : '/api/game/questions/';

  const response = await apiFetch(url, { method: 'GET' });

  if (!response.ok) {
    throw new Error(`Failed to fetch static questions: ${response.status}`);
  }

  return response.json();
}

export async function createStaticQuestion(
  payload: StaticQuestionCreatePayload,
): Promise<StaticQuestionDetail> {
  const response = await apiFetch('/api/game/questions/create/', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(err?.detail || `Failed to create question: ${response.status}`);
  }

  return response.json();
}

export async function getStaticQuestionDetail(id: number): Promise<StaticQuestionDetail> {
  const response = await apiFetch(`/api/game/questions/${id}/detail/`, { method: 'GET' });

  if (!response.ok) {
    throw new Error(`Failed to fetch question: ${response.status}`);
  }

  return response.json();
}

export async function updateStaticQuestion(
  id: number,
  payload: StaticQuestionCreatePayload,
): Promise<StaticQuestionDetail> {
  const response = await apiFetch(`/api/game/questions/${id}/detail/`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(err?.detail || `Failed to update question: ${response.status}`);
  }

  return response.json();
}

export async function deleteStaticQuestion(id: number): Promise<void> {
  const response = await apiFetch(`/api/game/questions/${id}/detail/`, { method: 'DELETE' });

  if (!response.ok) {
    throw new Error(`Failed to delete question: ${response.status}`);
  }
}

export async function bulkDeleteStaticQuestions(
  ids: number[],
): Promise<{ message: string; deleted_count: number }> {
  const response = await apiFetch('/api/game/questions/bulk-delete/', {
    method: 'DELETE',
    body: JSON.stringify({ ids }),
  });

  if (!response.ok) {
    throw new Error(`Failed to delete questions: ${response.status}`);
  }

  return response.json();
}

export interface SharedQuizAdminListItem extends SharedQuizMeta {
  created_by_username: string | null;
}

export interface SharedQuizWritePayload {
  title: string;
  description?: string;
  rounds?: number;
  questions_per_round?: number;
  is_active?: boolean;
  max_attempts_per_user?: number | null;
  join_code?: string;
  selected_question_ids: number[];
}

export async function listSharedQuizzes(): Promise<SharedQuizAdminListItem[]> {
  const response = await apiFetch('/api/game/custom-quizzes/', { method: 'GET' });
  if (!response.ok) {
    throw new Error(`Failed to fetch custom quizzes: ${response.status}`);
  }
  return response.json();
}

export async function getSharedQuizDetail(quizId: string): Promise<SharedQuizAdminListItem> {
  const response = await apiFetch(`/api/game/custom-quizzes/${quizId}/`, { method: 'GET' });
  if (!response.ok) {
    throw new Error(`Failed to fetch custom quiz: ${response.status}`);
  }
  return response.json();
}

export async function createSharedQuiz(payload: SharedQuizWritePayload): Promise<SharedQuizAdminListItem> {
  const response = await apiFetch('/api/game/custom-quizzes/', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(
      err?.join_code?.[0] || err?.selected_question_ids?.[0] || err?.detail || `Failed to create quiz: ${response.status}`
    );
  }
  return response.json();
}

export async function updateSharedQuiz(quizId: string, payload: SharedQuizWritePayload): Promise<SharedQuizAdminListItem> {
  const response = await apiFetch(`/api/game/custom-quizzes/${quizId}/`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(err?.selected_question_ids?.[0] || err?.detail || `Failed to update quiz: ${response.status}`);
  }
  return response.json();
}

export async function bulkDeleteSharedQuizzes(ids: string[]): Promise<{ message: string; deleted_count: number }> {
  const response = await apiFetch('/api/game/custom-quizzes/bulk-delete/', {
    method: 'DELETE',
    body: JSON.stringify({ ids }),
  });
  if (!response.ok) {
    throw new Error(`Failed to delete quizzes: ${response.status}`);
  }
  return response.json();
}

export interface QuizParticipant {
  user_id: number;
  display_name: string;
  email: string;
  attempts_used: number;
  best_score: number;
  extra_attempts: number;
  attempts_allowed: number | null;
}

export async function getQuizParticipants(quizId: string): Promise<{ quiz_id: string; results: QuizParticipant[] }> {
  const response = await apiFetch(`/api/game/custom-quizzes/${quizId}/participants/`, { method: 'GET' });
  if (!response.ok) {
    throw new Error(`Failed to fetch participants: ${response.status}`);
  }
  return response.json();
}

export async function setParticipantExtraAttempts(
  quizId: string,
  userId: number,
  extraAttempts: number,
): Promise<QuizParticipant> {
  const response = await apiFetch(`/api/game/custom-quizzes/${quizId}/participants/${userId}/attempts/`, {
    method: 'PUT',
    body: JSON.stringify({ extra_attempts: extraAttempts }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => null);
    throw new Error(err?.extra_attempts?.[0] || err?.detail || `Failed to update attempts: ${response.status}`);
  }
  return response.json();
}